import { useQuery } from '@tanstack/react-query';
import { Button } from '@finn-auto/design-system/atoms/button';
import { Spinner } from '@finn-auto/design-system/atoms/spinner';
import {
  fetchCandidateSchedule,
  ScheduleRefusedError,
  type CandidateSchedule,
  type TrailInterview,
} from '../../services/taCandidateScheduleApi';
import { COLORS, fmtSlotRange, fmtSlotStart } from './format';

/**
 * The candidate panel's interview trail: every round they have had or have coming.
 *
 * Cancellations and moves are the POINT of this section, not an edge case. They are what a
 * recruiter currently reconstructs by hand from Lever, the calendar and their own memory — and
 * they are the part Lever alone gets wrong, because our key cannot delete panels (the server reads
 * our own booking records alongside it; see proxy-server/lib/candidateSchedule.js).
 *
 * Fetched when the panel opens, never before.
 *
 * ⚠️ The cancellation reasons shown here are INTERNAL wording ("The role was closed or put on
 * hold"). They explain FINN to the audit trail, several of them would tell a candidate something
 * that is none of their business, and a recruiter's own screen is the only place they may appear.
 * Nothing in this section may be copied into a candidate-facing draft.
 */

const STATUS: Record<TrailInterview['status'], { label: string; bg: string; text: string }> = {
  upcoming: { label: 'Upcoming', bg: COLORS.blueBg, text: COLORS.blueText },
  held: { label: 'Held', bg: COLORS.greenBg, text: COLORS.greenText },
  cancelled: { label: 'Cancelled', bg: COLORS.redBg, text: COLORS.redText },
};

/**
 * "The candidate cancelled" reads very differently from "we cancelled" — so it says which.
 *
 * Null when our own records know nothing: Lever's bare flag means the cancellation is real but
 * unattributable, and a second line reading "Cancelled" under a pill that already says Cancelled
 * is noise pretending to be information.
 */
function cancelledBy(interview: TrailInterview): string | null {
  const c = interview.cancellation;
  if (!c) return null;
  if (c.source === 'candidate') return 'Cancelled by the candidate';
  if (c.by) return `Cancelled by ${c.by}`;
  if (c.source) return 'Cancelled by FINN';
  return null;
}

const when = (i: TrailInterview) =>
  i.at && i.endAt ? fmtSlotRange({ start: i.at, end: i.endAt }) : i.at ? fmtSlotStart(i.at) : 'Time unknown';

function TrailRow({ interview }: { interview: TrailInterview }) {
  const status = STATUS[interview.status];
  const by = cancelledBy(interview);
  return (
    <div className="rounded-lg p-3" style={{ backgroundColor: COLORS.cotton }}>
      <div className="flex flex-wrap items-center gap-2 text-xs text-gray-500">
        <span className="font-semibold text-gray-800">{interview.round || 'Interview'}</span>
        <span
          className={interview.status === 'cancelled' ? 'line-through' : undefined}
          /* Struck through rather than hidden: a cancelled interview still happened to the
             candidate's week, and its date is how a recruiter recognises which one it was. */
        >
          {when(interview)}
        </span>
        <span
          className="ml-auto shrink-0 rounded-full px-2 py-0.5 text-[11px] font-semibold"
          style={{ backgroundColor: status.bg, color: status.text }}
        >
          {status.label}
        </span>
      </div>

      {interview.interviewers.length > 0 && (
        <div className="mt-1 text-sm text-gray-700">{interview.interviewers.join(', ')}</div>
      )}

      {(by || interview.cancellation?.reason) && (
        <div className="mt-1 text-sm" style={{ color: COLORS.redText }}>
          {/* The reason is INTERNAL wording — see the warning at the top of this file. */}
          {[by, interview.cancellation?.reason].filter(Boolean).join(' — ')}
        </div>
      )}

      {interview.rescheduleCount > 0 && (
        <div className="mt-1 text-xs text-gray-500">
          Moved {interview.rescheduleCount}× before this time
        </div>
      )}
    </div>
  );
}

export function PanelSchedule({ boardEmail, opportunityId }: { boardEmail: string; opportunityId: string }) {
  const { data, error, isPending, refetch } = useQuery<CandidateSchedule, Error>({
    queryKey: ['candidate-schedule', boardEmail, opportunityId],
    queryFn: () => fetchCandidateSchedule({ opportunityId, boardEmail }),
    staleTime: 60_000,
    retry: (count, err) => !(err instanceof ScheduleRefusedError) && count < 1,
  });

  return (
    <section className="flex flex-col gap-3">
      <h3 className="text-sm font-semibold text-gray-900">🗓️ Interviews</h3>

      {isPending && (
        <p className="flex items-center gap-2 text-sm text-gray-500">
          <Spinner className="h-3 w-3" /> Loading the interview history…
        </p>
      )}

      {error instanceof ScheduleRefusedError && <p className="text-sm text-gray-600">{error.message}.</p>}
      {error && !(error instanceof ScheduleRefusedError) && (
        <div className="space-y-2">
          <p className="text-sm text-red-600">Could not load the interview history just now.</p>
          <Button size="sm" variant="outline" onClick={() => void refetch()}>
            Try again
          </Button>
        </div>
      )}

      {data && data.interviews.length === 0 && (
        <p className="text-sm text-gray-500">No interviews have been scheduled for this candidate yet.</p>
      )}

      {data && data.interviews.length > 0 && (
        <div className="space-y-2">
          {data.interviews.map((i) => (
            <TrailRow key={i.id} interview={i} />
          ))}
        </div>
      )}

      {/* Said plainly rather than silently: a trail missing its moves reads as a shorter history,
          not as a broken one, and nothing on screen would otherwise give it away. */}
      {data && !data.ourRecordsOk && (
        <p className="text-xs" style={{ color: COLORS.orangeText }}>
          Our own booking records could not be read just now — cancellations and moves may be missing.
        </p>
      )}
    </section>
  );
}
